// @ts-nocheck
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Package, Warehouse, AlertTriangle, Edit } from "lucide-react";
import { useWarehouseInventory, useWarehouses, useInventoryItems } from "@/hooks/useInventoryData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

const WarehouseStockTab: React.FC = () => {
  const { data: inventory, isLoading: invLoading } = useWarehouseInventory();
  const { data: warehouses, isLoading: whLoading } = useWarehouses();
  const { data: items } = useInventoryItems();
  const queryClient = useQueryClient();

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addWarehouseOpen, setAddWarehouseOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editQty, setEditQty] = useState(0);
  const [editNotes, setEditNotes] = useState("");

  // New item form
  const [newItem, setNewItem] = useState({ name: "", category: "Signage", description: "", unit_cost: "", reorder_url: "" });
  // New warehouse form
  const [newWarehouse, setNewWarehouse] = useState({ name: "", city: "", state: "", address: "", contact_name: "", contact_phone: "" });

  const isLoading = invLoading || whLoading;

  // Group inventory by warehouse
  const byWarehouse = warehouses?.map((wh) => ({
    warehouse: wh,
    items: (inventory || []).filter((inv: any) => inv.warehouse_id === wh.id),
  })) || [];

  const totalItems = (inventory || []).reduce((sum: number, inv: any) => sum + (inv.quantity || 0), 0);
  const zeroStock = (inventory || []).filter((inv: any) => inv.quantity === 0).length;

  const handleSaveCount = async (id: string) => {
    const { error } = await supabase
      .from("manage_warehouse_inventory")
      .update({ quantity: editQty, notes: editNotes, last_counted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { toast.error("Failed to update"); return; }
    toast.success("Count updated");
    setEditingRow(null);
    queryClient.invalidateQueries({ queryKey: ["warehouse-inventory"] });
  };

  const handleAddItem = async () => {
    if (!newItem.name.trim()) { toast.error("Name required"); return; }
    const { error } = await supabase.from("manage_inventory_items").insert({
      name: newItem.name,
      category: newItem.category,
      description: newItem.description || null,
      unit_cost: newItem.unit_cost ? parseFloat(newItem.unit_cost) : null,
      reorder_url: newItem.reorder_url || null,
    });
    if (error) { toast.error("Failed to add item"); return; }
    toast.success("Item added");
    setNewItem({ name: "", category: "Signage", description: "", unit_cost: "", reorder_url: "" });
    setAddItemOpen(false);
    queryClient.invalidateQueries({ queryKey: ["inventory-items"] });
  };

  const handleAddWarehouse = async () => {
    if (!newWarehouse.name.trim() || !newWarehouse.city.trim()) { toast.error("Name and city required"); return; }
    const { error } = await supabase.from("manage_warehouses").insert({
      name: newWarehouse.name,
      city: newWarehouse.city,
      state: newWarehouse.state || null,
      address: newWarehouse.address || null,
      contact_name: newWarehouse.contact_name || null,
      contact_phone: newWarehouse.contact_phone || null,
    });
    if (error) { toast.error("Failed to add warehouse"); return; }
    toast.success("Warehouse added");
    setNewWarehouse({ name: "", city: "", state: "", address: "", contact_name: "", contact_phone: "" });
    setAddWarehouseOpen(false);
    queryClient.invalidateQueries({ queryKey: ["warehouses"] });
  };

  const qtyColor = (qty: number) => {
    if (qty === 0) return "text-red-600 font-bold";
    if (qty < 5) return "text-yellow-600 font-semibold";
    return "text-green-600";
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Summary Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Package className="h-8 w-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{totalItems}</p>
              <p className="text-sm text-muted-foreground">Total Items in Stock</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Warehouse className="h-8 w-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{warehouses?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Warehouses</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <div>
              <p className="text-2xl font-bold">{zeroStock}</p>
              <p className="text-sm text-muted-foreground">Items at Zero Stock</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap">
        <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" />Add Item</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} /></div>
              <div><Label>Category</Label>
                <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Signage", "Equipment", "Banners", "Tables & Chairs", "Tech", "Supplies", "Merch", "Other"].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Input value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} /></div>
              <div><Label>Unit Cost ($)</Label><Input type="number" step="0.01" value={newItem.unit_cost} onChange={(e) => setNewItem({ ...newItem, unit_cost: e.target.value })} /></div>
              <div><Label>Reorder URL</Label><Input value={newItem.reorder_url} onChange={(e) => setNewItem({ ...newItem, reorder_url: e.target.value })} /></div>
              <Button onClick={handleAddItem} className="w-full">Add Item</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={addWarehouseOpen} onOpenChange={setAddWarehouseOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />Add Warehouse</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Warehouse</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={newWarehouse.name} onChange={(e) => setNewWarehouse({ ...newWarehouse, name: e.target.value })} /></div>
              <div><Label>City *</Label><Input value={newWarehouse.city} onChange={(e) => setNewWarehouse({ ...newWarehouse, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={newWarehouse.state} onChange={(e) => setNewWarehouse({ ...newWarehouse, state: e.target.value })} /></div>
              <div><Label>Address</Label><Input value={newWarehouse.address} onChange={(e) => setNewWarehouse({ ...newWarehouse, address: e.target.value })} /></div>
              <div><Label>Contact Name</Label><Input value={newWarehouse.contact_name} onChange={(e) => setNewWarehouse({ ...newWarehouse, contact_name: e.target.value })} /></div>
              <div><Label>Contact Phone</Label><Input value={newWarehouse.contact_phone} onChange={(e) => setNewWarehouse({ ...newWarehouse, contact_phone: e.target.value })} /></div>
              <Button onClick={handleAddWarehouse} className="w-full">Add Warehouse</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Warehouse Tabs */}
      {byWarehouse.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No warehouses yet. Add one above.</CardContent></Card>
      ) : (
        <Tabs defaultValue={byWarehouse[0]?.warehouse.id}>
          <TabsList>
            {byWarehouse.map(({ warehouse, items: whItems }) => (
              <TabsTrigger key={warehouse.id} value={warehouse.id} className="gap-1.5">
                <Warehouse className="h-4 w-4" />
                {warehouse.name}
                <Badge variant="secondary" className="ml-1 text-xs">{whItems.length}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {byWarehouse.map(({ warehouse, items: whItems }) => (
            <TabsContent key={warehouse.id} value={warehouse.id}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Warehouse className="h-5 w-5 text-primary" />
                    {warehouse.name}
                    <span className="text-sm font-normal text-muted-foreground">— {warehouse.city}{warehouse.state ? `, ${warehouse.state}` : ""}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {whItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">No items tracked at this warehouse yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item Name</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-center">Quantity</TableHead>
                            <TableHead>Last Counted</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {whItems.map((inv: any) => (
                            <TableRow key={inv.id}>
                              <TableCell className="font-medium">{inv.item?.name || "Unknown"}</TableCell>
                              <TableCell><Badge variant="outline">{inv.item?.category}</Badge></TableCell>
                              <TableCell className="text-center">
                                {editingRow === inv.id ? (
                                  <Input type="number" className="w-20 mx-auto" value={editQty} onChange={(e) => setEditQty(parseInt(e.target.value) || 0)} />
                                ) : (
                                  <span className={qtyColor(inv.quantity)}>{inv.quantity}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {inv.last_counted_at ? format(new Date(inv.last_counted_at), "MMM d, yyyy") : "Never"}
                              </TableCell>
                              <TableCell>
                                {editingRow === inv.id ? (
                                  <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Notes..." />
                                ) : (
                                  <span className="text-sm text-muted-foreground">{inv.notes || "—"}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {editingRow === inv.id ? (
                                  <div className="flex gap-1 justify-end">
                                    <Button size="sm" onClick={() => handleSaveCount(inv.id)}>Save</Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingRow(null)}>Cancel</Button>
                                  </div>
                                ) : (
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingRow(inv.id); setEditQty(inv.quantity); setEditNotes(inv.notes || ""); }}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default WarehouseStockTab;
